import type { FormEvent, ReactNode } from 'react';
import { Button } from './Button';
import { Card } from './Card';

export interface FormCardProps {
  /**
   * Title shown in the header (desktop only)
   */
  title: string;

  /**
   * Button text (e.g., "Calculate", "Save")
   */
  buttonText: string;

  /**
   * Loading state for the button
   */
  loading?: boolean;

  /**
   * Form submit handler
   */
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;

  /**
   * Form content
   */
  children: ReactNode;

  /**
   * Additional className for the card
   */
  className?: string;
}

/**
 * Get loading text for button based on button text
 * Maps specific button texts to their -ing form
 */
function getLoadingText(buttonText: string): string {
  const loadingTextMap: Record<string, string> = {
    'Calculate': 'Calculating…',
    'Save': 'Saving…',
  };
  return loadingTextMap[buttonText] ?? `${buttonText}…`;
}

/**
 * FormCard component - Consistent form layout with header button
 *
 * Features:
 * - Desktop: Button in card header with title and divider
 * - Mobile: Sticky button at bottom above footer menu
 * - Consistent styling across all form pages
 *
 * @example
 * ```tsx
 * <FormCard
 *   title="Household Details"
 *   buttonText="Calculate"
 *   loading={isLoading}
 *   onSubmit={handleSubmit}
 * >
 *   <PersonSection ... />
 *   <ChildrenSection ... />
 * </FormCard>
 * ```
 */
export function FormCard({
  title,
  buttonText,
  loading = false,
  onSubmit,
  children,
  className = '',
}: FormCardProps): React.JSX.Element {
  const loadingText = getLoadingText(buttonText);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Card padding="md" className={`md:sticky md:top-8 ${className}`}>
        {/* Header with Button - Desktop Only */}
        <div className="hidden md:flex items-center justify-between pb-4 border-b border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <Button
            type="submit"
            disabled={loading}
            variant="primary"
            className="px-8 py-2.5 font-semibold"
          >
            {loading ? loadingText : buttonText}
          </Button>
        </div>

        <div className="space-y-6">
          {children}
        </div>
      </Card>

      {/* Sticky Mobile Button - positioned above footer */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-gray-200 shadow-lg z-10" style={{ paddingBottom: '5rem' }}>
        <Button
          type="submit"
          disabled={loading}
          variant="primary"
          fullWidth
          size="lg"
        >
          {loading ? loadingText : buttonText}
        </Button>
      </div>

      {/* Spacer to prevent content being obscured by sticky button on mobile */}
      <div className="md:hidden h-24" aria-hidden="true" />
    </form>
  );
}
